import React, { useEffect, useRef, useState } from 'react';

// Sacred phrases that cycle during generation
const sacredPhrases = [
  'Invoking the sacred scripts...',
  'Channeling divine typography...',
  'Illuminating the manuscript...',
  'Forging letters in sacred fire...',
  'Blessing the glyphs...',
  'Awakening ancient letterforms...',
  'Inscribing with holy precision...',
  'Summoning typographic spirits...',
];

// Sound URLs - using Web Audio API for generated sounds
const createSacredSound = (audioContext) => {
  // Create a reverent ambient drone
  const oscillator1 = audioContext.createOscillator();
  const oscillator2 = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  
  oscillator1.type = 'sine';
  oscillator1.frequency.setValueAtTime(110, audioContext.currentTime); // Low A
  
  oscillator2.type = 'sine';
  oscillator2.frequency.setValueAtTime(165, audioContext.currentTime); // E (fifth)
  
  gainNode.gain.setValueAtTime(0, audioContext.currentTime);
  gainNode.gain.linearRampToValueAtTime(0.08, audioContext.currentTime + 0.5);
  
  oscillator1.connect(gainNode);
  oscillator2.connect(gainNode);
  gainNode.connect(audioContext.destination);
  
  return { oscillator1, oscillator2, gainNode };
};

const createChimeSound = (audioContext) => {
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  
  oscillator.type = 'sine';
  oscillator.frequency.setValueAtTime(880, audioContext.currentTime); // High A
  oscillator.frequency.exponentialRampToValueAtTime(440, audioContext.currentTime + 1);
  
  gainNode.gain.setValueAtTime(0.15, audioContext.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 1);
  
  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);
  
  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + 1);
};

export const SacredGenerationOverlay = ({ isVisible, onComplete }) => {
  const [currentPhrase, setCurrentPhrase] = useState(sacredPhrases[0]);
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const audioContextRef = useRef(null);
  const soundNodesRef = useRef(null);

  // Cycle through sacred phrases
  useEffect(() => {
    if (!isVisible) return;
    
    const interval = setInterval(() => {
      setPhraseIndex((prev) => {
        const next = (prev + 1) % sacredPhrases.length;
        setCurrentPhrase(sacredPhrases[next]);
        return next;
      });
    }, 2000);

    return () => clearInterval(interval);
  }, [isVisible]);

  // Handle sound
  useEffect(() => {
    if (!isVisible || !soundEnabled) return;

    try {
      // Create audio context
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      
      audioContextRef.current = new AudioContext();
      soundNodesRef.current = createSacredSound(audioContextRef.current);
      
      soundNodesRef.current.oscillator1.start();
      soundNodesRef.current.oscillator2.start();

      // Play occasional chimes
      const chimeInterval = setInterval(() => {
        if (audioContextRef.current && audioContextRef.current.state === 'running') {
          createChimeSound(audioContextRef.current);
        }
      }, 3000);

      return () => {
        clearInterval(chimeInterval);
        if (soundNodesRef.current) {
          soundNodesRef.current.gainNode.gain.linearRampToValueAtTime(
            0, 
            audioContextRef.current.currentTime + 0.5
          );
          setTimeout(() => {
            soundNodesRef.current?.oscillator1?.stop();
            soundNodesRef.current?.oscillator2?.stop();
            audioContextRef.current?.close();
          }, 500);
        }
      };
    } catch (error) {
      console.log('Audio not supported:', error);
    }
  }, [isVisible, soundEnabled]);

  // Enable sound on first user interaction
  const handleEnableSound = () => {
    setSoundEnabled(true);
  };

  if (!isVisible) return null;

  return (
    <div className="sacred-generation-overlay" onClick={handleEnableSound}>
      {/* Sacred light rays */}
      <div className="sacred-rays" />
      
      {/* Main sacred circle */}
      <div className="sacred-circle">
        {/* Floating particles */}
        <div className="sacred-particles">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="sacred-particle" />
          ))}
        </div>
        
        {/* Rotating rings */}
        <div className="sacred-ring-outer" />
        <div className="sacred-ring-middle" />
        <div className="sacred-ring-inner" />
        
        {/* Central symbol */}
        <div className="sacred-symbol">✦</div>
      </div>
      
      {/* Candle flames */}
      <div className="sacred-candle">
        <div className="candle-flame" />
        <div className="candle-flame" />
        <div className="candle-flame" />
      </div>
      
      {/* Sound wave visualization */}
      {soundEnabled && (
        <div className="sacred-sound-waves">
          {[...Array(7)].map((_, i) => (
            <div key={i} className="sound-wave-bar" />
          ))}
        </div>
      )}
      
      {/* Sacred text */}
      <div className="absolute bottom-1/4 left-1/2 -translate-x-1/2 text-center">
        <p className="sacred-text">{currentPhrase}</p>
        <div className="sacred-progress">
          <div className="sacred-progress-bar" />
        </div>
        
        {/* Sound toggle hint */}
        {!soundEnabled && (
          <p className="text-xs text-[#C2B49A]/50 mt-4 font-serif animate-pulse">
            Click anywhere to enable sacred sounds
          </p>
        )}
      </div>
    </div>
  );
};

// Smaller inline generation indicator for the generate button area
export const SacredGenerationIndicator = ({ isGenerating }) => {
  if (!isGenerating) return null;
  
  return (
    <div className="flex items-center gap-3">
      <div className="relative w-8 h-8">
        {/* Mini rotating ring */}
        <div 
          className="absolute inset-0 border border-[#D4AF37]/50 rounded-full"
          style={{ animation: 'sacredRotate 3s linear infinite' }}
        />
        <div 
          className="absolute inset-1 border border-[#722F37]/50 rounded-full"
          style={{ animation: 'sacredRotateReverse 2s linear infinite' }}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <span 
            className="text-[#D4AF37] text-sm"
            style={{ animation: 'sacredGlow 1.5s ease-in-out infinite' }}
          >
            ✦
          </span>
        </div>
      </div>
      <span className="text-sm font-cinzel text-[#D4AF37] animate-pulse">
        Generating...
      </span>
    </div>
  );
};

export default SacredGenerationOverlay;
